/**
 * The three prompts the MARA tab runs without a server. Each one becomes agent ops (packages/agent/src/ops.ts) for
 * every entry's Protein component, applied through applyAgentResult so the whole prompt is one undo step:
 *   surface  -> MolecularSurface (the 'surface' preset) on Protein
 *   vdW      -> Atomistic spacefill on Protein, cartoon off
 *   rainbow  -> Ribbon colored by atom serial, interpolated rainbow (the 'atomSerialRainbow' preset), ribbon on
 * The tool card mirrors how prod shows MARA's "Manage Scene Representations in Nanome Workspace" call
 * (vendor-ref/mara/mara/nanome/tools.py:368-389).
 */
import { COLORING_PRESETS, VISUALIZATION_PRESETS } from '@nanome-lite/look';
import type { Op } from '@nanome-lite/agent/ops';
import type { Component, VisualizationModel } from '@nanome-lite/state';

export const SCENE_TOOL = 'Manage Scene Representations in Nanome Workspace';

export interface BakedPrompt {
  id: 'surface' | 'vdw' | 'rainbow';
  text: string;
  /** What MARA answers once the ops are in. */
  reply: string;
  ops(proteins: Component[]): Op[];
}

const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;
const preset = (name: keyof typeof VISUALIZATION_PRESETS) => clone(VISUALIZATION_PRESETS[name]) as unknown as VisualizationModel;

export const BAKED_PROMPTS: readonly BakedPrompt[] = [
  {
    id: 'surface',
    text: 'Show the surface',
    reply: 'Added a molecular surface to the protein, colored by chain.',
    ops: proteins => proteins.map(c => ({ op: 'updateVisualization', componentSerial: c.serial, visualization: { ...preset('surface'), visible: true } })),
  },
  {
    id: 'vdw',
    text: 'Change the protein view to van der Waals',
    reply: 'The protein now shows every atom at its van der Waals radius. I turned the cartoon off so the spheres are easier to read.',
    ops: proteins => proteins.flatMap((c): Op[] => {
      const out: Op[] = [{ op: 'updateVisualization', componentSerial: c.serial, visualization: { ...preset('spacefill'), visible: true } }];
      for (const v of c.visualizations) {
        if (v.kind === 'Ribbon' && v.visible !== false) out.push({ op: 'updateVisualization', componentSerial: c.serial, visualization: { ...clone(v), visible: false } });
      }
      return out;
    }),
  },
  {
    id: 'rainbow',
    text: 'Color the ribbon in rainbow',
    reply: 'The ribbon now runs through the rainbow from the first residue (violet) to the last (red).',
    ops: proteins => proteins.map((c): Op => {
      const ribbon = c.visualizations.find(v => v.kind === 'Ribbon') ?? preset('cartoon');
      return { op: 'updateVisualization', componentSerial: c.serial, visualization: { ...clone(ribbon), visible: true, coloring: clone(COLORING_PRESETS.atomSerialRainbow) as unknown as VisualizationModel['coloring'] } };
    }),
  },
];

export function bakedPrompt(text: string): BakedPrompt | undefined {
  const t = text.trim().toLowerCase().replace(/[.!?]+$/, '');
  return BAKED_PROMPTS.find(p => p.text.toLowerCase() === t);
}

/** The components a baked prompt acts on: each entry's default 'Protein' component. */
export function proteinComponents(components: readonly Component[]): Component[] {
  return components.filter(c => c.name === 'Protein' && c.entrySerials.length === 1);
}
